import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged, signInWithPopup, signOut as firebaseSignOut, GoogleAuthProvider } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';

/**
 * Sign-in for annotating. Learners never need to sign in: the learner page,
 * dictionary and settings are public. Annotating writes to Firestore, so
 * it needs a signed-in user whose profile (users/{uid}) has role
 * 'annotator' or 'admin'. Roles are set by the admin in the Firebase
 * console. Firestore rules (firestore.rules) enforce the same split, so
 * this context only decides what the UI offers.
 *
 * Any failure to read the profile means "no annotator access". Never fall
 * back to a privileged role.
 */

export const ANNOTATOR_ROLES = ['annotator', 'admin'];

const AuthContext = createContext(undefined);

// First sign-in creates the profile as a learner; the rules reject any
// other role on create, so nobody can grant themselves annotator access.
async function loadRole(firebaseUser) {
  const userRef = doc(db, 'users', firebaseUser.uid);
  const snap = await getDoc(userRef);
  if (snap.exists()) {
    await setDoc(userRef, { lastLoginAt: serverTimestamp() }, { merge: true });
    return snap.data().role || 'learner';
  }
  await setDoc(userRef, {
    email: (firebaseUser.email || '').toLowerCase(),
    displayName: firebaseUser.displayName || null,
    photoURL: firebaseUser.photoURL || null,
    role: 'learner',
    createdAt: serverTimestamp(),
    lastLoginAt: serverTimestamp(),
  });
  return 'learner';
}

export function AuthProvider({ children }) {
  const [state, setState] = useState({ user: null, role: null, loading: true, error: null });

  useEffect(() => {
    let current = true;
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setState({ user: null, role: null, loading: false, error: null });
        return;
      }
      setState((s) => ({ ...s, loading: true }));
      try {
        const role = await loadRole(firebaseUser);
        // Ignore a slow profile load for a user who has since signed out.
        const stillSignedIn = auth.currentUser && auth.currentUser.uid === firebaseUser.uid;
        if (current && stillSignedIn) setState({ user: firebaseUser, role, loading: false, error: null });
      } catch (e) {
        console.error('Could not load user profile:', e);
        if (current) {
          setState({
            user: firebaseUser,
            role: null,
            loading: false,
            error: "Couldn't check your access. Please try again.",
          });
        }
      }
    });
    return () => { current = false; unsubscribe(); };
  }, []);

  // A popup rather than a redirect: hoja is served from hoja.rakesh-m.dev
  // while Firebase Auth lives on hoja-copilot.firebaseapp.com, and browsers
  // that partition third-party storage break the redirect flow across those
  // two sites.
  const signIn = useCallback(async () => {
    setState((s) => ({ ...s, error: null }));
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
    } catch (e) {
      if (e && e.code === 'auth/popup-closed-by-user') return;
      console.error('Sign-in failed:', e);
      setState((s) => ({ ...s, error: 'Sign-in failed. Please try again.' }));
    }
  }, []);

  const signOut = useCallback(() => firebaseSignOut(auth), []);

  const value = {
    ...state,
    isAnnotator: !!state.user && ANNOTATOR_ROLES.includes(state.role),
    signIn,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
